import { Accordion, AccordionDetails, AccordionSummary, Box, Divider, Paper, Typography, makeStyles } from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import dayjs from "dayjs";
import React from "react";

import theme from "@/theme";

type Row = {
  title: string;
  values: string[];
  type: "category" | "item" | "sum";
};

type Props = {
  rows: Row[];
  monthlyResults: unknown[];
  selectedStartingMonth: dayjs.Dayjs;
};

const useStyles = makeStyles(() => ({
  card: {
    borderRadius: 16,
    overflow: "hidden",
    border: `1px solid ${theme.palette.semantic["border/default"]}`,
  },
  cardHeader: {
    backgroundColor: theme.palette.semantic["surface/card"],
    padding: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  cardHeaderMonth: {
    fontWeight: 700,
    fontSize: 16,
    lineHeight: 1,
  },
  cardHeaderTotal: {
    fontWeight: 700,
    fontSize: 16,
    lineHeight: 1,
  },
  accordion: {
    boxShadow: "none",
    "&:before": {
      display: "none",
    },
    "&.Mui-expanded": {
      margin: 0,
    },
  },
  accordionSummary: {
    backgroundColor: theme.palette.common.white,
    padding: theme.spacing(0, 2),
    minHeight: 48,
    "&.Mui-expanded": {
      minHeight: 48,
    },
  },
  accordionSummaryContent: {
    margin: 0,
    "&.Mui-expanded": {
      margin: 0,
    },
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  accordionTitle: {
    fontWeight: 700,
    fontSize: 14,
    lineHeight: 1,
  },
  accordionValue: {
    fontWeight: 500,
    fontSize: 14,
    lineHeight: 1,
  },
  accordionDetails: {
    padding: theme.spacing(2 ),
    backgroundColor: theme.palette.semantic["surface/subtle"],
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
  },
  detailRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: theme.spacing(2),
  },
  detailLabel: {
    color: theme.palette.text.primary,
    fontWeight: 400,
    fontSize: 14,
    lineHeight: 1,
  },
  detailValue: {
    color: theme.palette.text.primary,
    fontWeight: 400,
    fontSize: 14,
    lineHeight: 1,
    whiteSpace: "nowrap",
  },
}));

type CategorySpec = {
  key: string;
  sourceTitle: string;
  displayTitle: string;
  totalRowTitle?: string;
};

const CATEGORY_SPECS: CategorySpec[] = [
  { key: "income", sourceTitle: "毎月の収入", displayTitle: "収入", totalRowTitle: "毎月の収入合計" },
  { key: "expense", sourceTitle: "毎月の支出", displayTitle: "支出", totalRowTitle: "毎月の支出合計" },
  { key: "loan", sourceTitle: "ローン", displayTitle: "ローン", totalRowTitle: "ローン返済（利息含む）" },
  { key: "misc_income", sourceTitle: "その他の収入", displayTitle: "その他の収入", totalRowTitle: "その他の収入合計" },
  { key: "misc_expense", sourceTitle: "その他の支出", displayTitle: "その他の支出", totalRowTitle: "その他の支出合計" },
  // その他の分類は不要だが、念の為残しておく
  // { key: "monthly_cashflow", sourceTitle: "毎月の収支", displayTitle: "毎月の収支" },
  // { key: "monthly_cashflow_after_loan", sourceTitle: "毎月の収支（ローン返済後）", displayTitle: "毎月の収支（ローン返済後）" },
  // { key: "down_payment", sourceTitle: "頭金・購入時諸費用", displayTitle: "頭金・購入時諸費用" },
  // { key: "cashflow_before_tax", sourceTitle: "収支（税前）", displayTitle: "収支（税前）" },
  // { key: "cumulative_cashflow_before_tax", sourceTitle: "累積収支（税前）", displayTitle: "累積収支（税前）" },
];

const parseRowsByCategory = (rows: Row[]) => {
  const categoryToRows = new Map<string, Row[]>();
  let currentCategory: string | null = null;

  rows.forEach((row) => {
    if (row.type === "category") {
      currentCategory = row.title;
      if (!categoryToRows.has(row.title)) categoryToRows.set(row.title, []);
      return;
    }
    if (!currentCategory) return;
    categoryToRows.get(currentCategory).push(row);
  });

  return categoryToRows;
};

const getMonthCountWithoutYearlySum = (monthlyResults: unknown[]) => {
  // useBalanceSheetData は最後尾に「年合計」を付与しているため、月次カードでは除外する
  return Math.max(0, monthlyResults.length - 1);
};

const pickTotalValue = (categoryRows: Row[], monthIndex: number, totalRowTitle?: string) => {
  if (totalRowTitle) {
    const totalRow = categoryRows.find((r) => r.type === "sum" && r.title === totalRowTitle);
    if (totalRow) return totalRow.values[monthIndex] ?? "";
  }
  const firstSum = categoryRows.find((r) => r.type === "sum");
  return firstSum?.values?.[monthIndex] ?? "";
};

const getTaxBeforeTotal = (categoryToRows: Map<string, Row[]>, monthIndex: number) => {
  const categoryRows = categoryToRows.get("収支（税前）");
  if (!categoryRows) return "";
  const row = categoryRows.find((r) => r.type === "sum" && r.title === "収支合計（税前）");
  return row?.values?.[monthIndex] ?? "";
};

export const MonthlyCashflowCards: React.FC<Props> = ({ rows, monthlyResults, selectedStartingMonth }) => {
  const classes = useStyles();
  const categoryToRows = React.useMemo(() => parseRowsByCategory(rows), [rows]);
  const monthCount = getMonthCountWithoutYearlySum(monthlyResults);

  if (monthCount === 0) return null;

  return (
    <Box display="flex" flexDirection="column" gridGap={theme.spacing(2)}>
      {Array.from({ length: monthCount }).map((_, monthIndex) => {
        const monthLabel = selectedStartingMonth.add(monthIndex, "month").format("YYYY/MM");
        const headerTotal = getTaxBeforeTotal(categoryToRows, monthIndex);

        return (
          <Paper key={monthLabel || monthIndex} className={classes.card}>
            <Box className={classes.cardHeader}>
              <Typography className={classes.cardHeaderMonth}>
                {monthLabel}
              </Typography>
              <Typography className={classes.cardHeaderTotal}>
                {headerTotal}
              </Typography>
            </Box>
            {CATEGORY_SPECS.map((spec) => {
              const categoryRows = categoryToRows.get(spec.sourceTitle) ?? [];
              const totalValue = pickTotalValue(categoryRows, monthIndex, spec.totalRowTitle);
              const items = categoryRows.filter((r) => r.type === "item");

              return (
                <React.Fragment key={spec.key}>
                  <Divider />
                  <Accordion className={classes.accordion}>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      className={classes.accordionSummary}
                      classes={{ content: classes.accordionSummaryContent }}
                    >
                      <Typography className={classes.accordionTitle}>
                        {spec.displayTitle}
                      </Typography>
                      <Typography className={classes.accordionValue}>
                        {totalValue}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails className={classes.accordionDetails}>
                      {items.map((item) => (
                        <Box key={item.title} className={classes.detailRow}>
                          <Typography className={classes.detailLabel}>
                            {item.title}
                          </Typography>
                          <Typography className={classes.detailValue}>
                            {item.values[monthIndex] ?? ""}
                          </Typography>
                        </Box>
                      ))}
                    </AccordionDetails>
                  </Accordion>
                </React.Fragment>
              );
            })}
          </Paper>
        );
      })}
    </Box>
  );
};
